/**
 * ================================================================================================
 *  THE PASSWORD FIELD — ONE COMPONENT, AND THE EYE IS PART OF IT.
 * ================================================================================================
 *
 * Owner instruction, 2026-10-25, verbatim:
 *
 *     *"Add an eye icon to password fields — invitation acceptance, sign-in, change and reset —
 *     through a shared component. Default hidden; the button toggles without clearing the value or
 *     submitting the form; accessible description and keyboard operation required; the password
 *     must not be written to logs or retained beyond need."*
 *
 * ── WHY A COMPONENT AND NOT A PROP ──────────────────────────────────────────────────────────────
 *
 * Because the part that is easy to get wrong is not the icon. It is `type="button"`, which is the
 * single line that decides whether clicking the eye reveals the password or SUBMITS THE FORM — a
 * `<button>` inside a `<form>` defaults to `type="submit"`, so an eye written per screen is three
 * chances to submit a half-typed password on the way to reading it back. Written once, it is written
 * once correctly, and the next password field in the product inherits that rather than re-deciding
 * it.
 *
 * ── THREE FIELDS, NOT FOUR, AND THE FOURTH IS NAMED RATHER THAN FAKED ───────────────────────────
 *
 * The instruction lists four places. The product has three:
 *
 *   · sign-in                 `SignInScreen`
 *   · invitation acceptance   `CredentialSetupScreen kind="invitation"`
 *   · password reset          `CredentialSetupScreen kind="password_reset"`
 *
 * There is **no change-password screen, because there is no change-password route.** The operations
 * table has `auth.password.reset_request` and `auth.password.reset_complete` and nothing else that
 * takes a password except `auth.session.create` and `auth.invitation.accept`. A signed-in person
 * changes their password by sending themselves a reset. Adding a fourth field here would be a
 * control with no server behind it, so the gap is reported instead.
 *
 * ── THE VALUE IS NOT HELD HERE, AND THAT IS THE "NOT RETAINED BEYOND NEED" HALF ─────────────────
 *
 * This component is CONTROLLED: `value` and `onChange` belong to the screen, which already holds
 * the password for exactly as long as its own `useState` lives and clears with the screen. The only
 * state added is a boolean. There is no second copy of the secret, nothing is written to
 * `localStorage`, nothing is put on `window`, and there is no `console` call anywhere in this file —
 * which is the whole of "must not be written to logs": the way a password reaches a log is a
 * well-meant `console.log` beside the input, and there is one place that could be and it is this
 * one.
 *
 * REVEALING RESETS TO HIDDEN on unmount by construction — the state does not outlive the screen —
 * so a revealed password cannot be left on screen by a navigation.
 *
 * ── THE ACCESSIBILITY, SPECIFICALLY ─────────────────────────────────────────────────────────────
 *
 *   · It is a real `<button>`, so Tab reaches it and Space and Enter operate it. No `onKeyDown`,
 *     because a `<div role="button">` with a key handler is how that gets half-implemented.
 *   · `aria-pressed` carries the STATE, so a screen reader says "pressed"/"not pressed" rather than
 *     leaving the label to describe two different situations with one sentence.
 *   · `aria-label` names the ACTION in office Hebrew, and changes with the state, because "הצגת
 *     הסיסמה" when the password is already showing is a wrong label rather than a terse one.
 *   · `aria-controls` points at the input, so the relationship is stated and not inferred.
 *   · The glyph is `aria-hidden`: it is the label's picture, and a screen reader reading "eye" after
 *     the label would be reading the decoration twice.
 *   · A `role="status"` line announces the change, because `aria-pressed` alone is not announced on
 *     every screen reader and the change is the thing the person needs to know about.
 *   · The label is a real `<label for>` and NOT a wrapper. A `<label>` around both the input and the
 *     button forwards a click on the button to the input as well, which is two actions from one
 *     click — so the association is made by id, with `useId` so two fields on one page cannot
 *     collide.
 */

import { useId, useState } from 'react';

export interface PasswordFieldProps {
  /** 09 §9.1 V4 — a noun phrase in office Hebrew. */
  readonly labelHe: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /**
   * `current-password` on sign-in, `new-password` where one is being set. It is REQUIRED rather than
   * defaulted: the wrong value here is what makes a password manager fill a new-password field with
   * the old password, and a default would pick that silently for whoever forgot to pass it.
   */
  readonly autoComplete: 'current-password' | 'new-password';
  readonly required?: boolean;
  /** One sentence under the field, when the screen has something true to say about the rule. */
  readonly hintHe?: string;
}

const SHOW_HE = 'הצגת הסיסמה';
const HIDE_HE = 'הסתרת הסיסמה';
const SHOWN_HE = 'הסיסמה מוצגת';
const HIDDEN_HE = 'הסיסמה מוסתרת';

export function PasswordField({
  labelHe,
  value,
  onChange,
  autoComplete,
  required = false,
  hintHe,
}: PasswordFieldProps): JSX.Element {
  /** DEFAULT HIDDEN. Stated as the initial value so it cannot be anything else. */
  const [revealed, setRevealed] = useState(false);
  const inputId = useId();
  const hintId = `${inputId}-hint`;

  return (
    <div className="field">
      <label className="field__label" htmlFor={inputId}>
        {labelHe}
      </label>

      <div className="field__password">
        <input
          id={inputId}
          className="field__input field__input--password"
          /*
           * THE ONE LINE THE WHOLE COMPONENT IS ABOUT. Nothing else changes: the same element keeps
           * its value, its selection and its focus across the switch, which is what "toggles without
           * clearing the value" means — React does not remount it, because the element and its key
           * are unchanged.
           */
          type={revealed ? 'text' : 'password'}
          /*
           * LTR, because a password is typed in Latin characters inside a Hebrew form, and an RTL
           * field would put the caret and the characters in an order nobody chose. It is on the
           * existing fields already and is carried over rather than reconsidered.
           */
          dir="ltr"
          autoComplete={autoComplete}
          autoCapitalize="none"
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(e.currentTarget.value)}
          {...(required ? { required: true } : {})}
          {...(hintHe === undefined ? {} : { 'aria-describedby': hintId })}
        />

        <button
          /*
           * `type="button"`. A `<button>` in a `<form>` is a SUBMIT button unless it says otherwise,
           * so without this line the eye would post the form — with a half-typed password, before
           * the person had read it back. See the header.
           */
          type="button"
          className="field__reveal"
          aria-pressed={revealed}
          aria-label={revealed ? HIDE_HE : SHOW_HE}
          aria-controls={inputId}
          onClick={() => setRevealed((previous) => !previous)}
        >
          {/*
            AN EYE, DRAWN RATHER THAN IMPORTED. The product ships no icon set for this and a glyph
            from the font would render differently on every platform. `aria-hidden` because the
            button's own label already says what it does.

            The slash is the HIDDEN state's second signal: §3 — never colour alone, and here, never
            shape alone either. Open eye ⇒ the password is showing; crossed eye ⇒ it is hidden.
          */}
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              d="M1.5 12S5.5 5.5 12 5.5 22.5 12 22.5 12 18.5 18.5 12 18.5 1.5 12 1.5 12Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="12" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.8" />
            {revealed ? null : (
              <path
                d="M4 20 20 4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            )}
          </svg>
        </button>
      </div>

      {hintHe === undefined ? null : (
        <span className="field__hint" id={hintId}>
          {hintHe}
        </span>
      )}

      {/*
        THE CHANGE IS ANNOUNCED. `aria-pressed` describes the control; this describes what happened
        to the password. It is visually hidden because the eye already says it on screen.
      */}
      <span className="visually-hidden" role="status">
        {revealed ? SHOWN_HE : HIDDEN_HE}
      </span>
    </div>
  );
}
